import { useState } from 'react';
import { Bookmark, Share2, Star, TrendingUp, TrendingDown, MoreVertical, Filter, ArrowUpDown } from 'lucide-react';
import { Typography } from '@mui/material';

const filters = [
    "All Analysis", "2026 Market Outlook", "AI Tech", "Basic Material", "Biotech",
    "Bonds", "Closed End Funds", "Commodities", "Communication Services", "Consumer",
];

const analysisData = [
    { id: 1, title: "Cabot: Maintaining Bearish Stance On Guidance Cut And Enduring Trade Turbulence", ticker: "CBT", author: "Individual Trader", time: "Today, 12:20am", sentiment: "bearish", rating: 4.5, saves: 234, shares: 89 },
    { id: 2, title: "Tesla's Q4 Results Beat Expectations: What This Means For EV Sector", ticker: "TSLA", author: "Market Analyst Pro", time: "Yesterday, 11:45pm", sentiment: "bullish", rating: 4.8, saves: 567, shares: 234 },
    { id: 3, title: "Federal Reserve Policy Impact on Tech Stocks: A Deep Dive Analysis", ticker: "QQQ", author: "Economic Insights", time: "Yesterday, 9:30pm", sentiment: "neutral", rating: 4.2, saves: 445, shares: 178 },
    { id: 4, title: "Biotech Breakthrough: Small Cap Companies Leading Innovation", ticker: "XBI", author: "Healthcare Specialist", time: "2 days ago", sentiment: "bullish", rating: 4.6, saves: 312, shares: 145 },
    { id: 5, title: "Commodity Markets Under Pressure: Gold vs Silver Outlook 2026", ticker: "GLD", author: "Commodity Expert", time: "2 days ago", sentiment: "bearish", rating: 4.1, saves: 289, shares: 92 },
    { id: 6, title: "Bond Market Signals: What Rising Yields Mean For Your Portfolio", ticker: "AGG", author: "Fixed Income Analyst", time: "3 days ago", sentiment: "neutral", rating: 4.4, saves: 401, shares: 167 },
    { id: 7, title: "AI Revolution Continues: Top 5 Stocks to Watch in Machine Learning", ticker: "NVDA", author: "Tech Innovator", time: "3 days ago", sentiment: "bullish", rating: 4.9, saves: 892, shares: 456 },
];

const topAnalysts = [
    { name: "Sarah Chen", return: "+127.3%", picks: 45, accuracy: "89%" },
    { name: "Michael Torres", return: "+98.7%", picks: 38, accuracy: "85%" },
    { name: "Emma Wilson", return: "+85.2%", picks: 52, accuracy: "82%" },
    { name: "David Kim", return: "+73.9%", picks: 41, accuracy: "80%" },
    { name: "Lisa Johnson", return: "+69.4%", picks: 47, accuracy: "78%" },
];

const AnalysisRow = ({ analysis }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const getSentimentColor = (sentiment) => {
        if (sentiment === "bullish") return "text-green-400 bg-green-500/10 border-green-500/20";
        if (sentiment === "bearish") return "text-red-400 bg-red-500/10 border-red-500/20";
        return "text-slate-400 bg-slate-500/10 border-slate-500/20";
    };

    const SentimentIcon = ({ sentiment }) => {
        if (sentiment === "bullish") return <TrendingUp className="w-4 h-4" />;
        if (sentiment === "bearish") return <TrendingDown className="w-4 h-4" />;
        return null;
    };

    return (
        <div className="border-b border-slate-700/50">
            <div className="flex gap-3 py-4 px-2 hover:bg-slate-700/30 rounded-lg transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 text-sm font-semibold flex-shrink-0">
                    {analysis.ticker.substring(0, 2)}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-1">
                        <h3 className="font-semibold text-slate-100 flex-1">{analysis.title}</h3>
                        <div className="relative">
                            <button onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }} className="p-1 hover:bg-slate-700 rounded">
                                <MoreVertical className="w-4 h-4 text-slate-400" />
                            </button>
                            {showMenu && (
                                <div className="absolute right-0 mt-1 w-48 bg-slate-800 rounded-lg shadow-xl border border-slate-700 py-1 z-10">
                                    <button className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700">View details</button>
                                    <button className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700">Follow analyst</button>
                                    <button className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-slate-700">Report</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="px-2 py-1 text-xs font-semibold bg-slate-700/50 border border-slate-600 rounded text-slate-300">{analysis.ticker}</span>
                        <span className={`px-2 py-1 text-xs font-semibold border rounded flex items-center gap-1 ${getSentimentColor(analysis.sentiment)}`}>
                            <SentimentIcon sentiment={analysis.sentiment} />
                            {analysis.sentiment}
                        </span>
                        <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs font-semibold text-slate-300">{analysis.rating}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                        <span>{analysis.time}</span>
                        <span>•</span>
                        <span>{analysis.author}</span>
                        <span>•</span>
                        <button onClick={(e) => { e.stopPropagation(); setIsSaved(!isSaved); }} className={`flex items-center gap-1 hover:text-blue-400 transition-colors ${isSaved ? 'text-blue-400' : ''}`}>
                            <Bookmark className={`w-3.5 h-3.5 ${isSaved ? 'fill-blue-400' : ''}`} />
                            {analysis.saves}
                        </button>
                        <span>•</span>
                        <button className="flex items-center gap-1 hover:text-slate-300 transition-colors">
                            <Share2 className="w-3.5 h-3.5" />
                            {analysis.shares}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function Analysis() {
    const [activeFilter, setActiveFilter] = useState(0);
    const [showSummaries, setShowSummaries] = useState(false);

    return (
        <div className="min-h-screen bg-slate-900 px-4 md:px-8 py-6">
            <div className="flex justify-between items-center mb-6 md:mt-8">
                <div>
                    <Typography
                        fontSize="var(--font-size-lg)"
                        fontWeight={600}
                        color="#e2e8f0"
                        mb={0.25}
                        letterSpacing="-0.01em"
                    >
                        Analysis
                    </Typography>
                    <p className="text-slate-400 text-sm">Track market sentiment and expert insights across sectors</p>
                </div>
                <div className="flex gap-2">
                    <button className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-2">
                        <Filter className="w-4 h-4" />
                        Filter
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Sort
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-2 mb-6">
                <div className="flex gap-1 overflow-x-auto flex-1 pb-2">
                    {filters.map((f, idx) => (
                        <button key={f} onClick={() => setActiveFilter(idx)} className={`px-3 py-1.5 text-xs font-medium border rounded whitespace-nowrap transition-colors ${idx === activeFilter ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'}`}>
                            {f}
                        </button>
                    ))}
                </div>
                <button className="px-3 py-1.5 text-xs font-semibold bg-slate-800 border border-slate-700 rounded whitespace-nowrap text-slate-300 hover:bg-slate-700 transition-colors">More</button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3">
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 md:p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold text-slate-100">{filters[activeFilter]} <span className="text-slate-400">({analysisData.length})</span></h2>
                            <button onClick={() => setShowSummaries(!showSummaries)} className={`px-4 py-2 text-sm font-semibold border rounded-lg transition-colors ${showSummaries ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'}`}>
                                {showSummaries ? 'Hide summaries' : 'Show summaries'}
                            </button>
                        </div>
                        <div className="border-t border-slate-700">
                            {analysisData.map((analysis) => <AnalysisRow key={analysis.id} analysis={analysis} />)}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="relative w-6 h-6 flex items-center justify-center">
                                <Bookmark className="w-6 h-6 fill-blue-500 text-blue-500" />
                                <Star className="w-3 h-3 fill-white text-white absolute" style={{ top: '46%', left: '50%', transform: 'translate(-50%, -45%)' }} />
                            </div>
                            <h2 className="text-lg font-bold text-slate-100">Top Analysts</h2>
                        </div>
                        <p className="text-sm text-slate-400 mb-4">Based on total return, conviction, picks, and success rate.</p>
                        <div className="space-y-3">
                            {topAnalysts.map((analyst, idx) => (
                                <div key={analyst.name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors">
                                    <span className={`font-bold min-w-[20px] ${idx < 3 ? 'text-blue-400' : 'text-slate-500'}`}>{idx + 1}</span>
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold">
                                        {analyst.name.split(" ").map(n => n[0]).join("")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-slate-200 truncate">{analyst.name}</div>
                                        <div className="text-xs text-slate-400">{analyst.picks} picks • {analyst.accuracy} accuracy</div>
                                    </div>
                                    <span className="font-bold text-sm text-green-400">{analyst.return}</span>
                                </div>
                            ))}
                        </div>
                        <button className="w-full mt-4 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                            See all top analysts
                        </button>
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <h2 className="text-lg font-bold text-slate-100 mb-4">Market Insights</h2>
                        <div className="space-y-4">
                            <div>
                                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Most Discussed</div>
                                <div className="font-semibold text-sm text-slate-200">AI & Technology</div>
                            </div>
                            <div className="border-t border-slate-700 pt-4">
                                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Trending Sentiment</div>
                                <div className="flex items-center gap-1">
                                    <TrendingUp className="w-4 h-4 text-green-400" />
                                    <span className="font-semibold text-sm text-green-400">Bullish (68%)</span>
                                </div>
                            </div>
                            <div className="border-t border-slate-700 pt-4">
                                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total Analysis Today</div>
                                <div className="font-semibold text-sm text-slate-200">1,247 reports</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <h2 className="text-lg font-bold text-slate-100 mb-4">Trending Topics</h2>
                        <div className="flex flex-wrap gap-2">
                            {["Fed Policy", "AI Stocks", "EV Market", "Crypto", "Energy", "Healthcare"].map((topic) => (
                                <span key={topic} className="px-3 py-1.5 text-xs font-medium bg-slate-700/50 text-slate-300 border border-slate-600 rounded-full hover:bg-slate-700 cursor-pointer transition-colors">
                                    {topic}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}